import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useLongPoolingQueryHelper } from '@/shared/hooks';
import { useAutoRefreshStore } from '@/shared/store/auto-refresh.store';

import { Response } from 'api/apiBase/Response';

import { getAccounts } from '../api';
import { CEX_ACCOUNT_AUTO_REFRESH_KEYS, CEX_ACCOUNT_QUERY_KEYS } from '../api.const';
import { GetAccountsRequest, GetAccountsResponse } from '../api.types';

type QueryOptions<T> = Omit<
  UseQueryOptions<Response<GetAccountsResponse>, Error, T>,
  'queryKey' | 'queryFn'
>;

/**
 * Query hook for fetching CEX accounts for a specific trading pair.
 * Supports request cancellation via AbortController.
 * Integrated with LongPooling for auto-refresh functionality.
 *
 * @param req - Request parameters: pairId, limit, and optional lastSeenId.
 * @param options - Optional react-query options (enabled, staleTime, etc.).
 * @returns UseQueryResult with accounts data.
 * @see https://api.dev.111pg.info/swagger/#/cex-pairs/CexPairsAccountsListRequest
 */
export function useGetAccounts<T = Response<GetAccountsResponse>>(
  req: GetAccountsRequest,
  options?: QueryOptions<T>,
) {
  const refreshCacheName = useMemo(
    () => CEX_ACCOUNT_AUTO_REFRESH_KEYS.accounts(req.pairId),
    [req.pairId],
  );
  const refreshInterval = useAutoRefreshStore(s => s.entities[refreshCacheName]?.interval ?? 0);
  const isAutoRefreshEnabled = refreshInterval > 0;

  const result = useQuery({
    queryKey: CEX_ACCOUNT_QUERY_KEYS.accounts(req),
    queryFn: ({ signal }) => getAccounts(req, signal),
    staleTime: isAutoRefreshEnabled ? 0 : 60000,
    refetchInterval: isAutoRefreshEnabled ? refreshInterval : false,
    ...options,
    // pairId can be 0 (valid ID), so check for undefined/null instead of > 0
    enabled: req.pairId != null && (options?.enabled ?? true),
  });

  useLongPoolingQueryHelper(
    refreshCacheName,
    result.isLoading || result.isFetching,
    result.refetch,
  );

  return result;
}
